import {
  AnnotationInformation,
  BugIcon,
  ChevronRightSmall,
  HelpCenterIcon,
  LinkExternal,
  SupportBubble,
  Users,
  YouTubeOutline,
} from 'blaze-ui/icons'
import { Section } from '@react-stately/collections'
import { Menu } from 'components/Menu/Menu'
import Styles from './HelpDropdown.module.scss'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { HELP_CENTER_URL } from 'utils/constants'
import { useAuth } from 'hooks/UseAuth'
import { checkFeatureFlagUserGroup, USERS } from 'utils/featureFlagUserGroups'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useProductTourObserver } from 'components/ProductTour/hooks/useProductTourObserver'
import { TOUR_NAMES } from 'components/ProductTour/Types'
import cx from 'classnames'
import { Key } from 'react'
import { ItemElement } from '@react-types/shared'
import { useReportBug } from 'components/BugReporter/useReportBug'
import { Item } from 'components/Menu/MenuItem'
import { MenuProps } from 'components/Menu/Types'
import { WizardType } from 'components/Wizards/schemas/Types'
import { WizardTourThumbnail } from 'components/Wizards/Wizard/WizardTour/WizardTour'
import { WIZARD_VIDEO_MAP } from 'components/Wizards/Wizard/WizardTour/Types'
import { useModals } from 'almanac-ui/Modal'
import { VideoModal } from 'components/VideoModal/VideoModal'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useIsVirtualMarketer } from 'hooks/useIsVirtualMarketer'

const AUTOPILOT_COMMUNITY_LINK =
  'https://community.blaze.ai/join?invitation_token=3d3365af96a4666fb91704734594ec7de35a0736-60ac922c-cccd-470d-a6d1-b0b6ab1ca942'
const COPILOT_COMMUNITY_LINK =
  'https://community.blaze.ai/join?invitation_token=46588f4bb1747e85787e48f8db61f1bb9d8cf4a7-973c6674-206d-4196-b546-0b143684682d'

export interface HelpSectionsProps extends Pick<MenuProps<any>, 'menuType'> {
  close: () => void
  videoTutorialType?: WizardType
}

export const HelpSections = ({ close, menuType, videoTutorialType }: HelpSectionsProps) => {
  const openWindowSafely = useOpenWindowSafely()
  const isWizardTourEnabled = useFlipperFlag('wizards_tour')
  const auth = useAuth()
  const { isMobile } = useResponsiveDesign()
  const isAlmanacUser = checkFeatureFlagUserGroup([USERS.withAlmanacEmail], auth?.email)
  const { isTabletPortrait } = useResponsiveDesign()
  const { availableTour, restartProductTour } = useProductTourObserver()
  const isVirtualMarketer = useIsVirtualMarketer()
  const { reportBug } = useReportBug()
  const { openModal } = useModals()

  const embedId = WIZARD_VIDEO_MAP[videoTutorialType as WizardType]
  const communityLink = isVirtualMarketer ? AUTOPILOT_COMMUNITY_LINK : COPILOT_COMMUNITY_LINK

  const handleWizardTour = () => {
    if (embedId) openModal(VideoModal, { embedId }, { newStack: true })
  }

  const navigateToHelpCenter = () => {
    openWindowSafely(HELP_CENTER_URL)
  }

  const navigateToYoutubeChannel = () => {
    openWindowSafely('https://www.youtube.com/@Blaze-AI')
  }

  const handleRestartTour = () => {
    if (availableTour) {
      restartProductTour(availableTour)
    }
  }

  const Chevron = menuType === 'tray' ? ChevronRightSmall : undefined

  return (
    <Menu
      menuType={menuType}
      aria-label="Help"
      className={cx(Styles.helpMenu, { [Styles.isMobile]: isMobile })}
      itemClassName={Styles.helpMenuItem}
      onAction={(action: Key) => {
        switch (action) {
          case 'help-center':
            navigateToHelpCenter()
            break
          case 'wizard-tour':
            handleWizardTour()
            break
          case 'join-community':
            openWindowSafely(communityLink)
            break
          case 'youtube-channel':
            navigateToYoutubeChannel()
            break
          case 'bug-reporter':
            reportBug()
            break
          case 'intercom-control':
            Intercom('show')
            break
          case 'show-onboarding':
            handleRestartTour()
            break
        }
        close()
      }}
    >
      {videoTutorialType && isWizardTourEnabled && (
        <Section>
          <Item key="wizard-tour" textValue="Wizard Tour">
            <WizardTourThumbnail type={videoTutorialType} className={Styles.wizardTourVideoThumbnail} />
          </Item>
        </Section>
      )}
      {availableTour && (
        <Section title="Product Tours">
          <Item key="show-onboarding" textValue="Show Onboarding" frontIcon={AnnotationInformation} endIcon={Chevron}>
            <span data-product-tour-restart={availableTour}>Start {TOUR_NAMES[availableTour]}</span>
          </Item>
        </Section>
      )}
      <Section title="Support">
        {
          [
            <Item key="youtube-channel" textValue="YouTube Channel" frontIcon={YouTubeOutline} endIcon={Chevron}>
              Blaze YouTube Channel
              <LinkExternal size={16} color="var(--dark-40)" />
            </Item>,
            <Item key="help-center" textValue="Help Center" frontIcon={HelpCenterIcon} endIcon={Chevron}>
              Help Center
              <LinkExternal size={16} color="var(--dark-40)" />
            </Item>,
            <Item key="join-community" textValue="Join Community" frontIcon={Users} endIcon={Chevron}>
              Join Our Community
              <LinkExternal size={16} color="var(--dark-40)" />
            </Item>,
            isAlmanacUser && !isTabletPortrait && (
              <Item key="bug-reporter" textValue="Report Bug" frontIcon={BugIcon} endIcon={Chevron}>
                Report Bug
              </Item>
            ),
            <Item key="intercom-control" textValue="Support" frontIcon={SupportBubble} endIcon={Chevron}>
              Support
            </Item>,
          ].filter(Boolean) as ItemElement<HTMLElement>[]
        }
      </Section>
    </Menu>
  )
}
